/**
 * ConsentAccordionRow — R5 consent row (Figma 1824:168730).
 *
 * A single optional-consent control rendered as a compact card row: a toggle
 * switch + bold title on the left, a chevron on the right, and a description
 * caption below. The caption is truncated to one line when collapsed and
 * expands in place when the chevron is toggled. The toggle switch (consent
 * on/off) is independent of the chevron (show/hide description).
 *
 * When `requireInteraction` is set, the on/off toggle is replaced by an
 * explicit Opt-In / Opt-Out radio pair (Opt-In === true, Opt-Out === false).
 * In that mode the row is tri-state: pass `selection` (`true` | `false` |
 * `null`) so an *unanswered* control (`null`) renders with neither radio
 * checked, forcing the provider to actively choose. When `selection` is
 * omitted the radios fall back to the boolean `enabled`. Setting
 * `errorMessage` renders an inline validation message below the radios (used
 * when submit is attempted with an unanswered required section).
 *
 * Presentation-only: the parent owns the value (via `onToggle`) and the
 * unanswered/error state.
 */

import { ChevronDown } from 'flowbite-react-icons/outline'
import { useId, useMemo, useState } from 'react'

import { sanitizeLimitedInlineHtml } from '@/utils/sanitizeLimitedInlineHtml'

interface ConsentAccordionRowProps {
  title: string
  /** Consent on/off. Used by the toggle mode and as the radio fallback when `selection` is omitted. */
  enabled: boolean
  onToggle: (value: boolean) => void
  /** Disclosure copy (limited inline HTML; sanitized before render). */
  descriptionHtml: string
  /** When true, the toggle is non-interactive (e.g. order in progress). */
  disabled?: boolean
  /** Start with the description expanded. */
  defaultExpanded?: boolean
  /**
   * When true, the consent toggle (the on/off switch) is hidden while the row
   * is collapsed and only appears once the row is expanded via the chevron.
   * Used for the New York sample-retention consent when the ordering
   * organization is not based in New York: the question collapses to just its
   * title (no visible check) until the user expands it. Defaults to `false`
   * (toggle always visible).
   */
  hideToggleWhenCollapsed?: boolean
  /**
   * Config-driven UI gating hint: when true, the provider must actively make a
   * selection for this consent before the order can be submitted. When true,
   * the row renders explicit Opt-In / Opt-Out radio buttons instead of the
   * single on/off toggle so an untouched control is visibly unanswered.
   * Defaults to `false`.
   */
  requireInteraction?: boolean
  /**
   * Tri-state selection for `requireInteraction` mode: `true` = Opt-In,
   * `false` = Opt-Out, `null`/`undefined` = unanswered (neither radio checked).
   * When omitted, the radios fall back to `enabled`. Ignored in toggle mode.
   */
  selection?: boolean | null
  /**
   * Inline validation message shown below the radios (e.g. after a blocked
   * submit) when the required section is unanswered. Only rendered in
   * `requireInteraction` mode.
   */
  errorMessage?: string
  /** Radio label for the opt-in choice (requireInteraction mode). Defaults to "Opt-In". */
  optInLabel?: string
  /** Radio label for the opt-out choice (requireInteraction mode). Defaults to "Opt-Out". */
  optOutLabel?: string
}

export function ConsentAccordionRow({
  title,
  enabled,
  onToggle,
  descriptionHtml,
  disabled = false,
  defaultExpanded = false,
  hideToggleWhenCollapsed = false,
  requireInteraction = false,
  selection,
  errorMessage,
  optInLabel = 'Opt-In',
  optOutLabel = 'Opt-Out',
}: ConsentAccordionRowProps) {
  const id = useId()
  const [expanded, setExpanded] = useState(defaultExpanded)
  const safeHtml = useMemo(() => sanitizeLimitedInlineHtml(descriptionHtml), [descriptionHtml])
  const panelId = `${id}-panel`
  const errorId = `${id}-error`
  // The check (toggle switch) is hidden only while collapsed when the caller
  // opts in; expanding the row always reveals it. The description preview is
  // likewise suppressed while collapsed in that mode, so a collapsed row shows
  // only its title (plus the chevron).
  const showToggle = !hideToggleWhenCollapsed || expanded
  const showDescription = !hideToggleWhenCollapsed || expanded
  const radioGroupName = `${id}-consent-choice`
  // Tri-state for the radio mode: prefer the explicit `selection` (which can be
  // `null` = unanswered); fall back to the boolean `enabled` when the caller
  // has not adopted tri-state yet. `resolvedSelection === null` renders both
  // radios unchecked so an untouched required section reads as unanswered.
  const resolvedSelection: boolean | null = selection === undefined ? enabled : selection
  const optInChecked = resolvedSelection === true
  const optOutChecked = resolvedSelection === false
  const hasError = requireInteraction && Boolean(errorMessage)

  return (
    <div
      className={`flex w-full flex-col gap-1 rounded-base border bg-white px-4 py-3.5 shadow-xs dark:bg-gray-900 sm:px-6 ${
        hasError
          ? 'border-red-500 dark:border-red-500'
          : 'border-border-base dark:border-gray-700'
      }`}
    >
      <div className="flex w-full items-center gap-4">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {!requireInteraction && showToggle ? (
            <span className={disabled ? 'inline-flex shrink-0 cursor-not-allowed' : 'inline-flex shrink-0'}>
              <button
                type="button"
                role="switch"
                aria-checked={enabled}
                aria-label={title}
                disabled={disabled}
                onClick={() => {
                  if (!disabled) onToggle(!enabled)
                }}
                className={`relative flex h-5 w-9 shrink-0 items-center rounded-full border p-px transition-colors ${
                  enabled ? 'justify-end' : 'justify-start'
                } ${
                  enabled
                    ? 'border-brand-600 bg-brand-600'
                    : 'border-border-base-strong bg-gray-200 dark:border-gray-600 dark:bg-gray-700'
                } ${disabled ? 'pointer-events-none opacity-60' : 'cursor-pointer'}`}
              >
                <span className="size-4 rounded-full bg-white shadow-sm transition-all" />
              </button>
            </span>
          ) : null}
          <p className="min-w-0 flex-1 text-sm font-medium leading-5 text-text-heading dark:text-white">
            {title}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls={panelId}
          aria-label={`${expanded ? 'Hide' : 'Show'} ${title} details`}
          className="shrink-0 rounded-full p-1 text-text-body transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </button>
      </div>
      {showDescription ? (
        <div className="flex items-start gap-2">
          <span className="size-4 w-9 shrink-0" aria-hidden />
          <p
            id={panelId}
            className={`min-w-0 flex-1 text-xs leading-5 text-text-body dark:text-gray-400 [&_b]:font-semibold [&_b]:text-text-heading dark:[&_b]:text-white [&_strong]:font-semibold [&_strong]:text-text-heading dark:[&_strong]:text-white ${
              expanded ? '' : 'truncate'
            }`}
            dangerouslySetInnerHTML={{ __html: safeHtml }}
          />
        </div>
      ) : null}
      {requireInteraction ? (
        <fieldset
          className="mt-2 flex flex-col gap-2 sm:flex-row sm:gap-6"
          disabled={disabled}
          aria-label={title}
          aria-invalid={hasError || undefined}
          aria-describedby={hasError ? errorId : undefined}
        >
          <label
            className={`inline-flex items-center gap-2 text-sm font-medium text-text-body dark:text-gray-400 ${
              disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
            }`}
          >
            <input
              type="radio"
              name={radioGroupName}
              checked={optInChecked}
              disabled={disabled}
              onChange={() => {
                if (!disabled) onToggle(true)
              }}
              className="size-4 shrink-0 border-border-base-strong text-brand-600 focus:ring-brand-600/20 dark:border-gray-600 dark:bg-gray-700"
            />
            <span>{optInLabel}</span>
          </label>
          <label
            className={`inline-flex items-center gap-2 text-sm font-medium text-text-body dark:text-gray-400 ${
              disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
            }`}
          >
            <input
              type="radio"
              name={radioGroupName}
              checked={optOutChecked}
              disabled={disabled}
              onChange={() => {
                if (!disabled) onToggle(false)
              }}
              className="size-4 shrink-0 border-border-base-strong text-brand-600 focus:ring-brand-600/20 dark:border-gray-600 dark:bg-gray-700"
            />
            <span>{optOutLabel}</span>
          </label>
        </fieldset>
      ) : null}
      {hasError ? (
        <p id={errorId} className="mt-1 text-xs font-medium text-red-600 dark:text-red-500">
          {errorMessage}
        </p>
      ) : null}
    </div>
  )
}
